import type { LucideIcon } from "lucide-react";
import { X } from "lucide-react";
import { type ReactNode, type RefObject, useEffect } from "react";

import { cn } from "@/utils/utils";

/**
 * The legacy notice modal: an icon in a tinted ring with pulsing circles
 * behind it, a title, a line or two of text, and the actions indented under
 * the text. The password-expiry warning and the reset-request receipt both
 * use it.
 *
 * A native `<dialog>`, so the focus trap, Esc, and the backdrop come from the
 * platform. The caller keeps the ref, so it can close the dialog itself and
 * route on `onClose`.
 */
const TONE = {
  primary: { ring: "border-accent-soft bg-accent-soft", glyph: "text-accent" },
  success: { ring: "border-success-soft bg-success-soft", glyph: "text-success" },
  warning: { ring: "border-warning-soft bg-warning-soft", glyph: "text-warning" },
} as const;

export function NoticeDialog({
  dialogRef,
  icon: Icon,
  tone = "primary",
  title,
  description,
  extra,
  actions,
  onClose,
  closable = true,
}: {
  dialogRef: RefObject<HTMLDialogElement | null>;
  icon: LucideIcon;
  /** The ring and the glyph. "warning" for a decision somebody is waiting on. */
  tone?: "primary" | "success" | "warning";
  title: string;
  description: string;
  /** A line under the description — the days remaining, say. */
  extra?: ReactNode;
  actions: ReactNode;
  onClose: () => void;
  closable?: boolean;
}) {
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, [dialogRef]);

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      aria-labelledby="notice-title"
      className="bg-surface-raised text-fg animate-sheet rounded-sheet m-auto w-[calc(100%-2rem)] max-w-[544px] overflow-hidden p-6 shadow-[var(--shadow-sheet)]"
    >
      {closable ? (
        <button
          type="button"
          onClick={() => dialogRef.current?.close()}
          className="text-fg-subtle hover:bg-surface-inset hover:text-fg absolute top-4 right-4 z-10 grid size-8 place-items-center rounded-lg transition-colors"
        >
          <X className="size-4.5" aria-hidden />
          <span className="sr-only">Dismiss</span>
        </button>
      ) : null}

      <div className="relative flex items-start gap-4 px-1.5 pt-1.5">
        <div className="relative size-16 shrink-0">
          {/* A zero-size anchor at the middle of the icon, so every ring is
              concentric with it. Each is pulled back by half its own size
              rather than by a transform: the pulse animates `transform`, and
              anything else living there is dropped the moment it stops. */}
          <div aria-hidden className="pointer-events-none absolute top-1/2 left-1/2 size-0">
            {[245, 190, 150, 94].map((size, index) => (
              <div
                key={size}
                className="border-line absolute animate-[circle-pulse_3s_ease-in-out_infinite] rounded-full border"
                style={{
                  width: size,
                  height: size,
                  marginLeft: -size / 2,
                  marginTop: -size / 2,
                  animationDelay: `${[0.6, 0.6, 0.3, 0][index]}s`,
                }}
              />
            ))}
          </div>
          <div
            className={cn(
              "relative z-10 grid size-16 place-items-center rounded-full border-8",
              TONE[tone].ring,
            )}
          >
            <Icon className={cn("size-6", TONE[tone].glyph)} aria-hidden />
          </div>
        </div>

        <div className="relative z-10 flex min-w-0 flex-1 flex-col gap-1 pr-8">
          <h2 id="notice-title" className="text-title-3">
            {title}
          </h2>
          <p className="text-fg-muted text-callout text-pretty">{description}</p>
          {extra}
        </div>
      </div>

      <div className="relative z-10 mt-4 flex items-center gap-3 pl-[85px] [&>*]:flex-1">
        {actions}
      </div>
    </dialog>
  );
}
